<template>

    <div class="row">
        <div class="col-lg-8 offset-lg-2 col-xl-6 offset-xl-3">

            <div>
                <div v-if="showPrepopMessage && affiliateLogoUrl" class="text-center mt-2 mb-2">
                    <small class="text-muted">{{ getTranslatedText('__you_were_referred_by__') + '...' }}</small><br>
                    <img :src="affiliateLogoUrl" loading="lazy" alt="partner logo" class="img-fluid my-2" style="max-width: 120px; max-height: 80px;"/>
                </div>

                <div v-if="applicationSettings.currentNotice" class="notice-inline mb-3">
                    <span v-html="applicationSettings.currentNotice"></span>
                </div>
                <div v-else-if="showPrepopMessage">
                    <div class="notice-inline mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" style="vertical-align: -3px; margin-right: 6px; flex-shrink: 0;"><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/></svg>
                        <span v-html="getTranslatedText('__from_prepop_get_loan__')"></span>
                    </div>
                </div>
            </div>

            <Notifications :notifications="applicationSettings.notifications" @dismiss="dismissNotification" />

            <PrepopWelcomeModal
                v-if="showPrepopWelcomeModal"
                @close="showPrepopWelcomeModal = false"
            />

            <div class="card shadow my-2 bg-body rounded h-auto py-4 px-3 py-md-4 px-md-5">

                <LoadingAnimation v-if="loadingAnimation" :showBrandLogo="showBrandLogo"
                                  :showAffiliateLogo="showAffiliateLogo"
                                  :brandLogo="projectBrand"
                                  :message="applicationSettings.loadingAnimationMessage ?? null"
                                  :image="applicationSettings.loadingAnimationImage ?? null"
                                  :animationType="applicationSettings.loadingAnimationType ?? 'spinner'"
                />

                <div v-show="!loadingAnimation" class="">

                    <RegularApplicationFlow v-if="showInitialFlow"
                        v-model:component-settings="initialFlowSettings"
                        :product-name="product"
                    />

                    <ProcessedApplication v-if="!showInitialFlow && hasProcessedApplication"
                        :application="processedApplication"
                        :mustRedirect="!!applicationSettings.mustRedirect"
                        :memberPortalUrl="applicationSettings.portalBaseUrl"
                    />

                </div>
            </div>
        </div>
    </div>
</template>

<script setup>

import { reactive, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

import LoadingAnimation from '@/components/main-app/LoadingAnimation.vue';
import Notifications from '@/components/main-app/Notifications.vue';
import PrepopWelcomeModal from '@/components/main-app/PrepopWelcomeModal.vue';

// flows
import RegularApplicationFlow from '@/components/flows/RegularApplicationFlow.vue';
import ProcessedApplication from '@/components/ProcessedApplication.vue';

import { useApplicationCore } from '@/composables/useApplicationCore.js';
import { useApplicationLifecycle } from '@/composables/useApplicationLifecycle.js';
import { createApplicationSettings, createFlowSettings } from '@/composables/useApplicationSettings.js';
import { provideApplicationSettings } from '@/composables/useApplicationSettingsProvider.js';

// Props
const props = defineProps({
    product: {
        type: String,
        default: 'personal_loan',
    },
    skipVerification: {
        type: Boolean,
        default: false,
    },
    portalBaseUrl: {
        type: String,
        default: 'https://compare.loanscanada.ca',
    },
    prepopFields: {
        type: String,
        default: '',
    },
    forceSmsVerification: {
        type: Boolean,
        default: false,
    },
    affiliateLogoUrl: {
        type: String,
        default: null,
    },
    country: {
        type: String,
        default: 'CA',
    },
    formNonce: {
        type: String,
        default: '',
    },
});

// Composables
const core = useApplicationCore(props);
const lifecycle = useApplicationLifecycle(core);

core.provideApp();

const { app, processedApplication, getTranslatedText } = core;

// Reactive state
const applicationSettings = reactive(createApplicationSettings(props));
provideApplicationSettings(applicationSettings);
const flowSettings = createFlowSettings();
const initialFlowSettings = reactive(flowSettings.initialFlowSettings);
const projectBrand = ref(null);
const showPrepopWelcomeModal = ref(false);
let prepopWelcomeTimerId = null;

// Methods
let loadingAnimationTimeoutId = null;

function executeLoadingAnimation(time) {
    if (time < 0) {
        applicationSettings.showLoadingAnimation = true;
    }
    else if (time == 0) {
        applicationSettings.showLoadingAnimation = false;
        if (loadingAnimationTimeoutId) {
            clearTimeout(loadingAnimationTimeoutId);
            loadingAnimationTimeoutId = null;
        }
    }
    else {
        // loading animation is gonna be set to a maximum of 30sec
        let timeInSeconds = (time < 30) ? (time * 1000) : 30000;
        applicationSettings.showLoadingAnimation = true;
        if (loadingAnimationTimeoutId) {
            clearTimeout(loadingAnimationTimeoutId);
        }
        loadingAnimationTimeoutId = setTimeout(() => {
            applicationSettings.showLoadingAnimation = false;
            loadingAnimationTimeoutId = null;
        }, timeInSeconds);
    }
}

function dismissNotification(type) {
    applicationSettings.notifications[type] = {};
}

// Computed
const showBrandLogo = computed(() => !!applicationSettings.showBrandLogoDuringAnimation);
const showAffiliateLogo = computed(() => !!applicationSettings.showAffiliateLogoDuringAnimation);
const loadingAnimation = computed(() => !!applicationSettings.showLoadingAnimation);
const showInitialFlow = computed(() => !initialFlowSettings.isCompleted);

const hasProcessedApplication = computed(() =>
    !!processedApplication.value && Object.keys(processedApplication.value).length > 0
);

const showPrepopMessage = computed(() => {
    let isFromRefreshResultsApp = app.value.hasOwnProperty('ra') ?? false;
    return !isFromRefreshResultsApp && props.prepopFields && props.prepopFields.length > 0 && (
        applicationSettings.stepOnFirstUserClick === null || applicationSettings.stepOnFirstUserClick === applicationSettings.progressBar.step
    );
});

// Lifecycle — top-level code in <script setup> runs during setup (equivalent to created)
lifecycle.onCreated(props);

onMounted(() => {
    lifecycle.onMounted(props, projectBrand);

    const isFromRefreshResultsApp = app.value.hasOwnProperty('ra') ?? false;
    const hasPrepop = !isFromRefreshResultsApp && props.prepopFields && props.prepopFields.length > 0;
    if (hasPrepop) {
        prepopWelcomeTimerId = setTimeout(() => {
            showPrepopWelcomeModal.value = true;
            prepopWelcomeTimerId = null;
        }, 1000);
    }
});

onBeforeUnmount(() => {
    if (prepopWelcomeTimerId) {
        clearTimeout(prepopWelcomeTimerId);
        prepopWelcomeTimerId = null;
    }
});

// Watchers
watch(
    () => applicationSettings.loadingAnimationSeconds,
    (newValue) => {
        lifecycle.createLoadingAnimationWatcher(executeLoadingAnimation)(newValue);
    }
);

watch(
    () => app.value.postal_code,
    (newValue) => {
        lifecycle.createPostalCodeWatcher()(newValue);
    }
);

</script>

<style scoped>
.notice-inline {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-left: 4px solid #10b981;
    border-radius: 0.5rem;
    padding: 0.85rem 1.25rem;
    text-align: center;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.6;
    color: #065f46;
}

.notice-inline :deep(small) {
    display: block;
    margin-top: 0.3rem;
    font-size: 0.875rem;
    font-weight: 400;
    color: #065f46;
    opacity: 0.9;
}
</style>
